import {TextNode} from 'lexical';
import {addClassNamesToElement} from '@lexical/utils';

const IS_TOKEN = 1;
const DATA_ENTITY_TYPE = 'MERGE_FIELD';

export class MergeFieldNode extends TextNode {
  constructor(text, dataKey, key, assignee, color) {
    super(text, key);
    this.__dataKey = dataKey;
    this.__mode = IS_TOKEN;
    this.__assignee = assignee || '';
    this.__color = color || '#2EC0D5';
  }

  get entityType() {
    return DATA_ENTITY_TYPE;
  }

  get assignable() {
    return [
      'issued_date',
      'signed_date',
      'assignee_name',
      'assignee_legal_name',
    ].includes(this.__dataKey);
  }

  get assigneeId() {
    return this.__assignee.assigneeType === 'manager' ? 'manager' : this.__assignee.employee?.id;
  }

  getAssignee() {
    return this.__assignee;
  }

  getColor() {
    return this.__color;
  }

  setAssignee(assignee) {
    const writable = this.getWritable();
    writable.__assignee = assignee;
  }

  setColor(color) {
    const writable = this.getWritable();
    writable.__color = color;
  }

  updateDOM(prevNode) {
    return prevNode.__text !== this.__text || prevNode.assigneeId !== this.assigneeId || prevNode.__color !== this.__color;
  }

  static getType() {
    return 'merge-field';
  }

  static clone(node) {
    return new MergeFieldNode(
      node.__text,
      node.__dataKey,
      node.__key,
      node.__assignee,
      node.__color
    );
  }

  createDOM(config) {
    const element = super.createDOM(config);
    element.setAttribute('data-entity-type', this.entityType);
    element.setAttribute('data-key', this.__dataKey);
    if (this.assignable && this.assigneeId) {
      element.setAttribute('data-assignee', this.assigneeId);
      element.setAttribute('data-color', this.__color);
      element.style.backgroundColor = this.__color;
    }
    addClassNamesToElement(element, this.getType());
    return element;
  }

  static importJSON(serializedNode) {
    const node = $createMergeFieldNode(
      serializedNode.text,
      serializedNode.dataKey,
      serializedNode.assignee,
      serializedNode.color
    );
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON() {
    return {
      detail: this.getDetail(),
      format: this.getFormat(),
      mode: this.getMode(),
      style: this.getStyle(),
      text: this.getTextContent(),
      type: this.getType(),
      dataKey: this.__dataKey,
      assignee: this.assigneeId,
      color: this.__color
    };
  }

  static importDOM() {
    return {
      span: (node) => {
        if (node.getAttribute('data-entity-type') === DATA_ENTITY_TYPE) {
          return {
            conversion: convertMergeFieldElement,
            priority: 4
          };
        }
        return null;
      }
    };
  }
}

function convertMergeFieldElement(domNode) {
  let textContent = domNode.textContent || '';
  let dataKey = domNode.getAttribute('data-key');
  let assigneeId = domNode.getAttribute('data-assignee');
  let color = domNode.getAttribute('data-color');

  return {
    node: $createMergeFieldNode(textContent, dataKey, assigneeId, color)
  };
}

export function $createMergeFieldNode(text, dataKey, assigneeId, color) {
  let assignee;

  if (assigneeId === 'manager') {
    assignee = {
      assigneeType: 'manager',
      employee: null
    };
  } else if (assigneeId) {
    assignee = {
      assigneeType: 'specific_employee',
      employee: {
        id: assigneeId
      }
    };
  } else {
    assignee = {
      assigneeType: 'employee',
      employee: null
    };
  }

  return new MergeFieldNode(text, dataKey, null, assignee, color);
}

export function $isMergeFieldNode(node) {
  return node instanceof MergeFieldNode;
}
